'use client';

import { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';

interface NavigationLoaderProps {
  children: React.ReactNode;
}

export default function NavigationLoader({ children }: NavigationLoaderProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const [isPageReady, setIsPageReady] = useState(false);
  const pathname = usePathname();
  const navigationStartTime = useRef<number>(0);

  // Listen for navigation start events
  useEffect(() => {
    const handleNavigationStart = () => {
      setIsNavigating(true);
      setIsPageReady(false);
      navigationStartTime.current = Date.now();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('navigation-start', handleNavigationStart);
      
      return () => {
        window.removeEventListener('navigation-start', handleNavigationStart);
      };
    }
  }, []);

  // Hide loader when pathname changes AND page is fully ready
  useEffect(() => {
    if (isNavigating) {
      // Wait for the new page to be completely rendered and painted
      const hideLoader = () => {
        // Wait for multiple animation frames to ensure content is painted
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Additional delay to ensure all content is visible
              setTimeout(() => {
                // Ensure minimum loading time for smooth UX
                const elapsedTime = Date.now() - navigationStartTime.current;
                const minLoadingTime = 800; // Increased for better perception
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
                
                setTimeout(() => {
                  setIsPageReady(true);
                  setIsNavigating(false);
                }, remainingTime);
              }, 200); // Additional buffer for content rendering
            });
          });
        });
      };

      // Create observer to detect when new content is added to DOM
      const observer = new MutationObserver((mutations) => {
        // Check if significant DOM changes occurred (new page content)
        const hasSignificantChanges = mutations.some(
          mutation => mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0
        );
        
        if (hasSignificantChanges) {
          // Disconnect observer and wait for rendering
          observer.disconnect();
          hideLoader();
        }
      });

      // Start observing DOM changes
      if (typeof document !== 'undefined') {
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      }

      // Fallback: hide after pathname change + time buffer
      const fallbackTimer = setTimeout(() => {
        observer.disconnect();
        hideLoader();
      }, 1500); // Fallback timeout

      return () => {
        observer.disconnect();
        clearTimeout(fallbackTimer);
      };
    }
  }, [pathname, isNavigating]);

  return (
    <div className="relative">
      {/* Main content */}
      <div className={`transition-all duration-300 ${isNavigating ? 'blur-sm opacity-60 pointer-events-none' : ''}`}>
        {children}
      </div>

      {/* Loading overlay */}
      {isNavigating && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 max-w-sm mx-4">
            {/* Spinner */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 border-4 border-brand-secondary/30 border-t-[#3F74EF] rounded-full animate-spin"></div>
            </div>
            
            {/* Loading text */}
            <div className="text-center">
              <h3 className="text-xl font-semibold text-white mb-2">
                Loading Page
              </h3>
              <p className="text-sm text-gray-300">
                Please wait while we prepare your content...
              </p>
            </div>
            
            {/* Progress dots */}
            <div className="flex justify-center space-x-2 rtl:space-x-reverse mt-6">
              <div className="w-2 h-2 bg-brand-secondary rounded-full animate-pulse" style={{ animationDelay: '0s' }}></div>
              <div className="w-2 h-2 bg-[#8b5cf6] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-[#a855f7] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

